import { useEffect, useRef, useState } from "react"

const ETHIOPIA_TIMEZONE = "Africa/Addis_Ababa"

/** Normalized instant + Ethiopia-local metadata (what hooks and callers use). */
export interface ServerTimeData {
  iso: string
  epochMs: number
  epochSeconds: number
  timezone: string
  offsetMinutes: number
  formatted: string
  dateYmd: string
}

/** Current API envelope + nested blocks; also accepts legacy flat `data`. */
interface ServerTimeApiEnvelope {
  success: boolean
  data?: {
    utc?: {
      iso?: string
      epochMs?: number
      formatted?: string
      timezone?: string
    }
    ethiopia?: {
      formatted?: string
      timezone?: string
      offsetHours?: number
    }
    server?: {
      timezone?: string
      offsetMinutes?: number
    }
    iso?: string
    epochMs?: number
    epochSeconds?: number
    timezone?: string
    offsetMinutes?: number
  }
}

export function getEthiopiaDateYmd(epochMs: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: ETHIOPIA_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(epochMs))
}

/** Date whose local getters reflect Ethiopia wall clock (not for instant comparisons). */
export function getEthiopiaWallClockDate(epochMs: number): Date {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: ETHIOPIA_TIMEZONE,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  })
  const parts = formatter.formatToParts(new Date(epochMs))
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0)

  return new Date(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second")
  )
}

function parseEthiopiaFormatted(formatted: string, offsetHours: number): number | null {
  const match = formatted.match(/^(\d{2})\/(\d{2})\/(\d{4}),\s*(\d{2}):(\d{2}):(\d{2})$/)
  if (!match) return null

  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  const second = Number(match[6])

  if ([month, day, year, hour, minute, second, offsetHours].some(Number.isNaN)) {
    return null
  }

  return Date.UTC(year, month - 1, day, hour - offsetHours, minute, second)
}

function normalizeServerTimeData(raw: ServerTimeApiEnvelope["data"]): ServerTimeData {
  if (!raw) {
    throw new Error("Invalid server time response")
  }

  const ethiopiaFormatted = raw.ethiopia?.formatted ?? ""
  const offsetHours =
    typeof raw.ethiopia?.offsetHours === "number" && !Number.isNaN(raw.ethiopia.offsetHours)
      ? raw.ethiopia.offsetHours
      : 3

  const isoFromUtc = raw.utc?.iso
  const isoLegacy = raw.iso
  const isoCandidate = isoFromUtc ?? isoLegacy

  let epochMs = raw.utc?.epochMs ?? raw.epochMs

  if ((epochMs == null || Number.isNaN(epochMs)) && ethiopiaFormatted.length > 0) {
    const parsedFromEthiopia = parseEthiopiaFormatted(ethiopiaFormatted, offsetHours)
    if (parsedFromEthiopia != null) {
      epochMs = parsedFromEthiopia
    }
  }

  if (typeof isoCandidate === "string" && isoCandidate.length > 0) {
    const parsed = Date.parse(isoCandidate)
    if (!Number.isNaN(parsed) && (epochMs == null || Number.isNaN(epochMs))) {
      epochMs = parsed
    }
  }

  if (epochMs == null || Number.isNaN(epochMs)) {
    throw new Error("Invalid server time response")
  }

  const instant = new Date(epochMs)
  if (Number.isNaN(instant.getTime())) {
    throw new Error("Invalid server time response")
  }

  const iso =
    typeof isoCandidate === "string" &&
    isoCandidate.length > 0 &&
    !Number.isNaN(Date.parse(isoCandidate))
      ? isoCandidate
      : instant.toISOString()

  const timezone = raw.ethiopia?.timezone ?? ETHIOPIA_TIMEZONE
  const offsetMinutes = offsetHours * 60

  let epochSeconds = raw.epochSeconds
  if (epochSeconds == null || Number.isNaN(epochSeconds)) {
    epochSeconds = Math.floor(epochMs / 1000)
  }

  const dateYmd = getEthiopiaDateYmd(epochMs)
  const formatted =
    ethiopiaFormatted.length > 0
      ? ethiopiaFormatted
      : getEthiopiaWallClockDate(epochMs).toLocaleString("en-US", {
          timeZone: ETHIOPIA_TIMEZONE,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })

  return {
    iso,
    epochMs,
    epochSeconds,
    timezone,
    offsetMinutes,
    formatted,
    dateYmd,
  }
}

const SUCCESS_REFRESH_INTERVAL_MS = 30000
const ERROR_RETRY_INTERVAL_MS = 2000

/**
 * Fetch server time from API
 */
async function fetchServerTime(): Promise<ServerTimeData> {
  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
  const url = apiUrl ? `${apiUrl}/v1/reports/server-time` : 'https://qetero.com/api/v1/reports/server-time'
  
  const res = await fetch(url, { 
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache'
    }
  })

  if (!res.ok) {
    throw new Error(`Failed to fetch server time: ${res.statusText}`)
  }

  const json = (await res.json()) as ServerTimeApiEnvelope
  if (!json.success || !json.data) {
    throw new Error('Invalid server time response')
  }

  return normalizeServerTimeData(json.data)
}

/**
 * Caching disabled by design.
 */
export function getCachedServerTime(): ServerTimeData | null {
  return null
}

/**
 * Synchronous server time is unavailable when caching is disabled.
 */
export function getServerTimeSync(): Date {
  return getEthiopiaWallClockDate(Date.now())
}

/**
 * React hook to fetch fresh server time (no cache)
 */
export function useServerTime() {
  const [serverTime, setServerTime] = useState<ServerTimeData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRetrying, setIsRetrying] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const hasSuccessfulFetchRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    let timerId: number | null = null

    const scheduleNext = (ms: number) => {
      if (cancelled) return
      timerId = window.setTimeout(loadServerTime, ms)
    }

    const loadServerTime = async () => {
      try {
        const fresh = await fetchServerTime()
        if (!cancelled) {
          setServerTime(fresh)
          hasSuccessfulFetchRef.current = true
          setError(null)
          setIsRetrying(false)
          setIsLoading(false)
          scheduleNext(SUCCESS_REFRESH_INTERVAL_MS)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err)
          setIsRetrying(true)
          if (!hasSuccessfulFetchRef.current) {
            setIsLoading(true)
          }
          scheduleNext(ERROR_RETRY_INTERVAL_MS)
        }
      }
    }

    loadServerTime()

    return () => {
      cancelled = true
      if (timerId !== null) {
        window.clearTimeout(timerId)
      }
    }
  }, [])

  const getServerDate = (): Date => {
    if (serverTime) {
      return getEthiopiaWallClockDate(serverTime.epochMs)
    }
    return getEthiopiaWallClockDate(Date.now())
  }

  const getServerDateYmd = (): string => {
    if (serverTime) {
      return serverTime.dateYmd
    }
    return getEthiopiaDateYmd(Date.now())
  }

  const getServerTimeMs = (): number => {
    if (serverTime) {
      return serverTime.epochMs
    }
    return Date.now()
  }

  const getServerTimeSeconds = (): number => {
    if (serverTime) {
      return serverTime.epochSeconds
    }
    return Math.floor(Date.now() / 1000)
  }

  return {
    serverTime,
    serverDate: getServerDate(),
    serverDateYmd: getServerDateYmd(),
    serverTimeMs: getServerTimeMs(),
    serverTimeSeconds: getServerTimeSeconds(),
    hasServerTime: serverTime !== null,
    isLoading,
    isRetrying,
    error,
  }
}
